import { useEffect, useRef, useState } from "react";
import { cachedGet, peekCache } from "@/lib/publicCache";

/**
 * Cached GET for public feeds/pages.
 * Uses a request id so fast navigation / Strict Mode never leaves ready=false stuck.
 */
export function useCachedGet(url, params, { enabled = true } = {}) {
  const paramsKey = JSON.stringify(params || null);
  const reqId = useRef(0);
  const [data, setData] = useState(() => (enabled ? peekCache(url, params) ?? null : null));
  const [ready, setReady] = useState(() => (enabled ? peekCache(url, params) !== undefined : false));

  useEffect(() => {
    if (!enabled || !url) return;

    const id = ++reqId.current;
    const cached = peekCache(url, params);

    if (cached !== undefined) {
      setData(cached);
      setReady(true);
      return;
    }

    setReady(false);

    cachedGet(url, params)
      .then((next) => {
        if (id !== reqId.current) return;
        setData(next);
        setReady(true);
      })
      .catch(() => {
        if (id !== reqId.current) return;
        setData(false);
        setReady(true);
      });
  }, [url, paramsKey, enabled, params]);

  return { data, ready, setData };
}
